import { useTranslation } from 'react-i18next';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import loginIllustration from '@interviewgator/brand/login.png';
import registerIllustration from '@interviewgator/brand/register.png';
import { NavBar } from '../components/shared/NavBar.js';
import { selectNeedsEmailVerification, useAuthStore } from '../store/authStore.js';

export function AuthLayout() {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const isRegister = pathname.includes('/register');
  const illustrationSrc = isRegister ? registerIllustration : loginIllustration;
  const user = useAuthStore((s) => s.user);
  const isInitialized = useAuthStore((s) => s.isInitialized);
  const needsEmailVerification = useAuthStore(selectNeedsEmailVerification);
  const isVerifyRoute = pathname === '/verify-email';

  // Signed-in users don't belong on guest pages (login/register). Redirect them
  // to the app — or to activation if their email isn't confirmed yet. The
  // activation page (/verify-email) is exempt: it owns its own redirects, and
  // bouncing it here would ping-pong with RequireAuth into a blank page.
  if (isInitialized && user && !isVerifyRoute) {
    return <Navigate to={needsEmailVerification ? '/verify-email' : '/dashboard'} replace />;
  }

  return (
    <div data-theme="dark" className="relative min-h-screen bg-ig-bg-primary text-ig-text-primary">
      <div className="absolute inset-x-0 top-0 z-50">
        <NavBar
          variant="app"
          appBorderClassName="border-b border-[#1F1F1F]"
          hideGuestAuthButtons
          showGuestLanguageButton
          restricted={isVerifyRoute}
        />
      </div>
      <a
        href="#auth-v2-main"
        className="sr-only z-50 rounded-md bg-ig-bg-elevated px-4 py-2 text-body-14 text-ig-text-primary focus:not-sr-only focus:fixed focus:left-4 focus:top-4"
      >
        {t('common.skipToContent', { defaultValue: 'Skip to content' })}
      </a>

      <div className="md:grid md:min-h-screen md:grid-cols-2">
        <aside
          className="relative hidden overflow-hidden bg-ig-bg-secondary p-8 md:flex md:flex-col md:justify-between lg:p-12"
          aria-label={t('auth.brandPanel', { defaultValue: 'Brand panel' })}
        >
          <div
            className="pointer-events-none absolute inset-0 bg-center bg-cover bg-no-repeat opacity-45"
            style={{ backgroundImage: `url(${illustrationSrc})` }}
            aria-hidden
          />
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                'radial-gradient(120% 100% at 50% 35%, rgba(0,0,0,0.16) 0%, rgba(0,0,0,0.58) 72%, rgba(0,0,0,0.78) 100%)',
            }}
            aria-hidden
          />

          <div />

          <div className="relative z-10 flex flex-col items-center gap-[30px]">
            <blockquote className="w-[360px] whitespace-pre-line text-center font-display text-[24px] font-light italic leading-[1.4] text-ig-text-primary">
              {t(isRegister ? 'auth.branding.quotes.register' : 'auth.branding.quotes.login')}
            </blockquote>
            <p className="font-display text-[14px] font-normal text-ig-text-primary/85">{t('auth.branding.quotes.attribution')}</p>
          </div>

          <div className="relative z-10 flex flex-wrap items-center justify-center gap-6">
            <span className="inline-flex items-center gap-1.5 rounded-sm border border-white/30 bg-black/20 px-3.5 py-1.5 font-sans text-label-11 font-normal uppercase text-ig-text-primary/90">
              <span className="h-1.5 w-1.5 rounded-full bg-ig-accent" />
              {t('auth.branding.pills.sessions')}
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-sm border border-white/30 bg-black/20 px-3.5 py-1.5 font-sans text-label-11 font-normal uppercase text-ig-text-primary/90">
              <span className="h-1.5 w-1.5 rounded-full bg-ig-accent" />
              {t('auth.branding.pills.languages')}
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-sm border border-white/30 bg-black/20 px-3.5 py-1.5 font-sans text-label-11 font-normal uppercase text-ig-text-primary/90">
              <span className="h-1.5 w-1.5 rounded-full bg-ig-accent" />
              {t('auth.branding.pills.results')}
            </span>
          </div>
        </aside>

        <main
          id="auth-v2-main"
          className="bg-ig-bg-primary px-6 py-6 md:flex md:min-h-screen md:flex-col md:px-10 md:py-4 lg:px-16 lg:py-6"
        >
          <div className="mx-auto flex w-full max-w-[520px] flex-1 items-center">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
